import { GOALS_HORIZON_YEARS } from '@map-my-money/views';
import { useDeferredValue, useId, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { api, upgradeOf } from '../api/client.ts';
import type { UpgradeRequired } from '../api/types.ts';
import { CompareChart } from '../components/charts/CompareChart.tsx';
import { ChatBanner, ResultStamp } from '../components/ChatLink.tsx';
import { TimeframePicker } from '../components/forecastControls.tsx';
import { AskAssistant } from '../components/Assistant.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { PageHeader } from '../components/Shell.tsx';
import { UpgradeNotice } from '../components/UpgradePrompt.tsx';
import { Field, Modal, SectionHeader, Toggle } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { todayDay, todayString } from '../lib/clock.ts';
import { compactMoney, money, monthYear, span } from '../lib/format.ts';
import { useIsDesktop, useLocalPreference } from '../lib/hooks.ts';
import {
  KINDS, PERCENT_RANGE, applySummary, applyToPlan, changeLabel, changeValueLabel, changeWhen, endingDebts, investments, isActive, makePercent,
  makePreset, makeRedirect, percentAmounts, percentItems, scenarioView, startChoices,
  type Change, type ChangeKind, type PercentChange, type PresetChange, type PresetKind, type RedirectChange, type ScenarioGoal, type ScenarioView,
} from '../lib/scenario.ts';
import type { WirePlan } from '../api/types.ts';
import { parseDay, type Day } from '@map-my-money/engine';
import { usePlayground } from '../state/playground.ts';
import { useActivePlan, usePlan, useStore } from '../state/store.tsx';
import { useToast } from '../state/toast.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';
import { replayRequest } from '../lib/chatReplay.ts';
import { WhatIfReplay } from './WhatIfReplay.tsx';

const KIND_ICON: Record<ChangeKind, IconName> = {
  raise: 'trendUp', side: 'wallet', spendLess: 'arrowDown', invest: 'bars', windfall: 'gift', child: 'baby', car: 'car', redirect: 'branch', percent: 'trend',
};
const PRESETS: readonly PresetKind[] = ['raise', 'side', 'spendLess', 'invest', 'windfall', 'child', 'car'];

/** A change's value is coloured by its effect on you: money freed is mint even when it's a spending cut. */
const effectTone = (kind: PresetKind) => {
  const sign = KINDS[kind].cashSign;
  if (sign > 0) return 'mint';
  if (sign < 0) return kind === 'car' ? 'terracotta' : 'amber';
  return 'violet';
};

const TYPE_TONE: Record<string, 'mint' | 'amber' | 'violet'> = { income: 'mint', expense: 'amber', investment: 'violet' };

/** More pay or less spending frees money (mint); more spending costs it (amber); investing moves it (violet). */
function percentTone(type: string | undefined, percent: number): string {
  if (type === 'investment') return 'violet';
  return (type === 'income') === percent > 0 ? 'mint' : 'amber';
}

function useScenario(changes: readonly Change[], years: number): ScenarioView | null {
  const { plan, waypoints } = useStore();
  const deferred = useDeferredValue(changes);
  const today = todayDay();
  return useMemo(() => (plan ? scenarioView(plan, waypoints, deferred, today, years) : null), [plan, waypoints, deferred, today, years]);
}

export function WhatIfScreen() {
  useDocumentTitle(t('nav.whatIf'));
  const { search } = useLocation();
  const request = useMemo(() => replayRequest(search), [search]);
  const desktop = useIsDesktop();
  if (request.kind === 'changes' || request.kind === 'buy') return <WhatIfReplay request={request} />;
  const note = request.kind === 'invalid' ? t(request.what === 'changes' ? 'chat.replay.invalidChanges' : 'chat.replay.invalidPurchase') : null;
  return desktop ? <WhatIfStudio note={note} /> : <QuickCalculator note={note} />;
}

function GoalImpactText({ g }: { g: ScenarioGoal }) {
  if (g.becomesAchievable) return <span className="impact impact--up">{t('whatIf.nowReachable')}</span>;
  if (g.monthsFaster === null || g.monthsFaster === 0) return <span className="impact impact--none">{t('common.noChange')}</span>;
  if (g.monthsFaster > 0) return <span className="impact impact--up"><span aria-hidden="true">↗ </span>{t('whatIf.faster', { span: span(g.monthsFaster) })}</span>;
  return <span className="impact impact--down"><span aria-hidden="true">↘ </span>{t('whatIf.slower', { span: span(-g.monthsFaster) })}</span>;
}

/** The goal's date with the changes, in the words every surface uses (R26). */
function goalDate(g: ScenarioGoal): string {
  if (g.modifiedDate === null) return t('goals.beyond', { years: GOALS_HORIZON_YEARS });
  return g.modifiedDate <= todayDay() ? t('goals.achieved') : t('goals.onCourse', { date: monthYear(g.modifiedDate) });
}

const goalIcon = (series: string): IconName => (series === 'debt' ? 'card' : series === 'investment' ? 'bars' : 'goals');

function GoalImpactList({ goals }: { goals: readonly ScenarioGoal[] }) {
  return (
    <ul className="impact-list">
      {goals.map((g) => (
        <li key={g.goal.id} className="impact-list__item">
          <span className={`impact-list__icon impact-list__icon--${g.goal.series}`} aria-hidden="true"><Icon name={goalIcon(g.goal.series)} size={15} /></span>
          <span className="impact-list__label">
            {g.goal.label}
            <span className="impact-list__date">{goalDate(g)}</span>
          </span>
          <GoalImpactText g={g} />
        </li>
      ))}
    </ul>
  );
}

function useApply(changes: readonly Change[], reset: () => void) {
  const { savePlan } = useStore();
  const { canEdit } = useActivePlan();
  const wire = usePlan();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const active = changes.filter(isActive);
  const apply = async () => {
    const today = todayDay();
    const next = applyToPlan(wire, active, today);
    if (active.length === 0 || !next) return;
    setBusy(true);
    const ok = await savePlan(next.plan, applySummary(active, today, wire), next.changesFrom);
    setBusy(false);
    if (ok) {
      toast.push({ tone: 'success', message: t('whatIf.applied') });
      reset();
    }
  };
  return { apply, busy, canApply: canEdit && active.length > 0 };
}

// MARK: Desktop studio

interface SavedScenario {
  readonly id: string;
  readonly name: string;
  readonly changes: readonly Change[];
}

const STARTERS = (): Change[] => [makePreset('raise', 0), makePreset('spendLess', 0), makePreset('invest', 0)];

function WhatIfStudio({ note }: { note: string | null }) {
  const [changes, setChanges] = useState<Change[]>(STARTERS);
  const [years, setYears] = useState(10);
  const playground = usePlayground();
  // A persona's scenarios stay out of the visitor's own list (same origin, same storage).
  const [saved, setSaved] = useLocalPreference<SavedScenario[]>(playground ? 'mm.try.whatif.scenarios' : 'mm.whatif.scenarios', []);
  const [sharing, setSharing] = useState(false);
  const [naming, setNaming] = useState(false);
  const view = useScenario(changes, years);
  const today = todayDay();
  const { apply, busy, canApply } = useApply(changes, () => setChanges(STARTERS()));
  const toast = useToast();
  const changesId = useId();
  const wire = usePlan();
  if (!view) return null;

  const update = (next: Change) => setChanges((cs) => cs.map((c) => (c.id === next.id ? next : c)));
  const remove = (id: string) => setChanges((cs) => cs.filter((c) => c.id !== id));
  const add = (change: Change | null) => change && setChanges((cs) => [...cs, change]);

  return (
    <div className="page whatif-desktop">
      <PageHeader
        eyebrow={t('whatIf.eyebrow')}
        title={t('whatIf.title')}
        subtitle={t('whatIf.subtitle')}
        actions={
          <>
            <button type="button" className="button button--outline" onClick={() => (playground ? playground.explainOwnPlan('share') : setSharing(true))} disabled={!canApply}>
              <Icon name="share" size={16} />
              {t('whatIf.share')}
            </button>
            <button type="button" className="button button--outline" onClick={() => setNaming(true)} disabled={!canApply}>
              {t('whatIf.saveScenario')}
            </button>
            <button type="button" className="button button--primary button--glow" onClick={() => void apply()} disabled={!canApply || busy}>
              <Icon name="check" size={16} strokeWidth={2.5} />
              {t('whatIf.apply')}
            </button>
          </>
        }
      />
      <ChatBanner note={note} />
      <div className="whatif-desktop__grid">
        <section className="whatif-changes" aria-labelledby={changesId}>
          <h2 className="eyebrow" id={changesId}>{t('whatIf.changes')}</h2>
          {changes.length === 0 && <p className="muted whatif-changes__empty">{t('whatIf.noChanges')}</p>}
          {changes.map((c) => (
            <ChangeCard key={c.id} change={c} view={view} wire={wire} today={today} onChange={update} onRemove={() => remove(c.id)} />
          ))}
          <div className="whatif-presets" role="group" aria-label={t('whatIf.presets')}>
            <span className="whatif-presets__label"><Icon name="plus" size={14} strokeWidth={2.5} />{t('whatIf.addChange')}</span>
            {PRESETS.map((kind) => (
              <button key={kind} type="button" className="preset-chip" onClick={() => add(makePreset(kind))}>
                {t(`whatIf.kind.${kind}`)}
              </button>
            ))}
            <StepChips wire={wire} today={today} onAdd={add} />
          </div>
        </section>

        <section className="card card--bento whatif-compare" aria-label={t('whatIf.withChanges')}>
          <div className="whatif-compare__top">
            <TimeframePicker value={years} onChange={setYears} options={[5, 10, 25, 50]} size="sm" />
            <ul className="legend">
              <li><span className="legend__dash" aria-hidden="true" />{t('whatIf.current')}</li>
              <li><span className="legend__swatch legend__swatch--line" style={{ background: 'var(--cyber)' }} aria-hidden="true" />{t('whatIf.withChanges')}</li>
            </ul>
          </div>
          <CompareChart
            baseline={view.baseline}
            modified={view.modified}
            height={420}
            zoom
            diffHeight={64}
            label={t('whatIf.chart.label', { years, baseline: money(view.baselineFinal), modified: money(view.modifiedFinal) })}
          />
          <div className="stat-tiles">
            <div className="stat-tile">
              <span className="stat-tile__label">{t('whatIf.extraPerMonth')}</span>
              <span className={`stat-tile__value ${view.extraPerMonth >= 0 ? 'text-mint' : 'text-amber'}`}>{money(view.extraPerMonth, { sign: true })}</span>
            </div>
            <div className="stat-tile">
              <span className="stat-tile__label">{t('whatIf.netWorthIn', { years })}</span>
              <span className="stat-tile__value text-cyber">{compactMoney(view.modifiedFinal)}</span>
            </div>
            <div className="stat-tile">
              <span className="stat-tile__label">{t('whatIf.betterOffBy')}</span>
              <span className={`stat-tile__value ${view.impact >= 0 ? 'text-mint' : 'text-amber'}`}>
                <span aria-hidden="true">{view.impact >= 0 ? '↗ ' : '↘ '}</span>{compactMoney(view.impact, { sign: true })}
              </span>
            </div>
          </div>
          <p className="hint-line">
            <span className="source-badge source-badge--claude">Claude</span>
            {t('whatIf.claudeHint')}
          </p>
        </section>

        <aside className="whatif-rail">
          <section className="card card--pad-lg">
            <SectionHeader title={t('whatIf.goalImpact')} />
            <GoalImpactList goals={view.goals} />
          </section>
          <section className="card card--pad-lg card--grow">
            <SectionHeader title={t('whatIf.compare')} />
            {saved.length === 0 ? (
              <p className="muted">{t('whatIf.compare.none')}</p>
            ) : (
              <ul className="scenario-list">
                {saved.map((s, i) => (
                  <SavedRow key={s.id} scenario={s} letter={String.fromCharCode(65 + i)} years={years} onLoad={() => setChanges(s.changes.map((c) => ({ ...c })))} onRemove={() => setSaved(saved.filter((x) => x.id !== s.id))} />
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>
      {naming && (
        <NameDialog
          onClose={() => setNaming(false)}
          onSave={(name) => {
            setSaved([...saved, { id: Date.now().toString(36), name, changes: changes.filter(isActive) }]);
            setNaming(false);
            toast.push({ tone: 'success', message: t('whatIf.scenarioSaved') });
          }}
        />
      )}
      {sharing && <ShareDialog changes={changes} years={years} onClose={() => setSharing(false)} />}
      <AskAssistant view="whatIf" />
      <ResultStamp result={null} />
    </div>
  );
}

function SavedRow({ scenario, letter, years, onLoad, onRemove }: { scenario: SavedScenario; letter: string; years: number; onLoad: () => void; onRemove: () => void }) {
  const view = useScenario(scenario.changes, years);
  return (
    <li className="scenario-row">
      <button type="button" className="scenario-row__main" onClick={onLoad} aria-label={`${t('whatIf.compare.load')} ${scenario.name}`}>
        <span className="scenario-row__name">{letter} · {scenario.name}</span>
        <span className="scenario-row__meta">{t('whatIf.compare.netWorth', { years })}</span>
      </button>
      <span className="scenario-row__value">{view ? compactMoney(view.modifiedFinal) : ''}</span>
      <button type="button" className="icon-button icon-button--sm" aria-label={t('whatIf.compare.remove', { name: scenario.name })} onClick={onRemove}>
        <Icon name="close" size={12} />
      </button>
    </li>
  );
}

interface CardProps<C extends Change> {
  readonly change: C;
  readonly view: ScenarioView;
  readonly wire: WirePlan;
  readonly today: Day;
  readonly onChange: (next: Change) => void;
  readonly onRemove: () => void;
}

function ChangeCard(props: CardProps<Change>) {
  const { change } = props;
  if (change.kind === 'redirect') return <RedirectCard {...props} change={change} />;
  if (change.kind === 'percent') return <PercentCard {...props} change={change} />;
  return <PresetCard {...props} change={change} />;
}

function CardHead({ change, label, tone, value, valueTone, htmlFor, onRemove }: { change: Change; label: string; tone: string; value: string | null; valueTone: string; htmlFor?: string; onRemove: () => void }) {
  return (
    <div className="change-card__head">
      <span className={`change-card__icon change-card__icon--${tone}`} aria-hidden="true"><Icon name={KIND_ICON[change.kind]} size={16} /></span>
      {htmlFor ? <label htmlFor={htmlFor} className="change-card__label">{label}</label> : <span className="change-card__label">{label}</span>}
      <span className={`change-card__value ${value === null ? 'change-card__value--none' : `text-${valueTone}`}`}>{value ?? t('common.noChange')}</span>
      <button type="button" className="icon-button icon-button--sm" aria-label={t('whatIf.removeChange', { name: label })} onClick={onRemove}>
        <Icon name="close" size={12} />
      </button>
    </div>
  );
}

function Skipped({ change, view }: { change: Change; view: ScenarioView }) {
  return view.skipped.includes(change.id) ? <p className="change-card__note" role="status">{t('whatIf.step.skipped')}</p> : null;
}

function PresetCard({ change, view, wire, today, onChange, onRemove }: CardProps<PresetChange>) {
  const spec = KINDS[change.kind];
  const id = useId();
  const label = changeLabel(change, today, wire);
  const value = change.amount === 0 ? null : changeValueLabel(change, wire, today);
  return (
    <div className="change-card">
      <CardHead change={change} label={label} tone={spec.tone} value={value} valueTone={effectTone(change.kind)} htmlFor={id} onRemove={onRemove} />
      <input
        id={id}
        type="range"
        className={`slider slider--${spec.tone}`}
        min={spec.min}
        max={spec.max}
        step={spec.step}
        value={change.amount}
        onChange={(e) => onChange({ ...change, amount: Number(e.target.value) })}
        aria-valuetext={value ?? t('common.noChange')}
        style={{ ['--fill' as string]: `${((change.amount - spec.min) / (spec.max - spec.min)) * 100}%` }}
      />
      <Skipped change={change} view={view} />
    </div>
  );
}

function RedirectCard({ change, view, wire, today, onChange, onRemove }: CardProps<RedirectChange>) {
  const debts = endingDebts(wire, today);
  const targets = investments(wire, today);
  const debtId = useId();
  const intoId = useId();
  const label = changeLabel(change, today, wire);
  const when = changeWhen(change, wire, today);
  return (
    <div className="change-card" data-testid="change-redirect">
      <CardHead change={change} label={label} tone="violet" value={changeValueLabel(change, wire, today) || null} valueTone="violet" onRemove={onRemove} />
      <div className="change-card__fields change-card__fields--stacked">
        <Field label={t('whatIf.step.debt')} htmlFor={debtId}>
          <select id={debtId} className="input" value={change.from} onChange={(e) => onChange({ ...change, from: e.target.value })}>
            {debts.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </Field>
        <Field label={t('whatIf.step.into')} htmlFor={intoId}>
          <select id={intoId} className="input" value={change.to} onChange={(e) => onChange({ ...change, to: e.target.value })}>
            {targets.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select>
        </Field>
      </div>
      {when && <p className="change-card__when">{when}</p>}
      <Skipped change={change} view={view} />
    </div>
  );
}

function PercentCard({ change, view, wire, today, onChange, onRemove }: CardProps<PercentChange>) {
  const items = percentItems(wire, today);
  const item = items.find((i) => i.id === change.item);
  const itemId = useId();
  const startsId = useId();
  const sliderId = useId();
  const label = changeLabel(change, today, wire);
  const value = change.percent === 0 ? null : changeValueLabel(change, wire, today);
  const amounts = change.percent === 0 ? null : percentAmounts(change, wire, view.changed, today);
  const fill = ((change.percent - PERCENT_RANGE.min) / (PERCENT_RANGE.max - PERCENT_RANGE.min)) * 100;
  return (
    <div className="change-card" data-testid="change-percent">
      <CardHead change={change} label={label} tone={TYPE_TONE[item?.type ?? 'expense'] ?? 'amber'} value={value} valueTone={percentTone(item?.type, change.percent)} onRemove={onRemove} />
      <div className="change-card__fields">
        <Field label={t('whatIf.step.item')} htmlFor={itemId}>
          <select id={itemId} className="input" value={change.item} onChange={(e) => onChange({ ...change, item: e.target.value })}>
            {items.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select>
        </Field>
        <Field label={t('whatIf.step.starts')} htmlFor={startsId}>
          <select id={startsId} className="input" value={change.starts ?? ''} onChange={(e) => onChange({ ...change, starts: e.target.value || null })}>
            <option value="">{t('whatIf.step.today')}</option>
            {startChoices(today).map((d) => <option key={d} value={d}>{monthYear(parseDay(d))}</option>)}
          </select>
        </Field>
      </div>
      <input
        id={sliderId}
        type="range"
        className="slider slider--violet"
        min={PERCENT_RANGE.min}
        max={PERCENT_RANGE.max}
        step={PERCENT_RANGE.step}
        value={change.percent}
        onChange={(e) => onChange({ ...change, percent: Number(e.target.value) })}
        aria-label={t('whatIf.step.byAria', { item: label })}
        aria-valuetext={value ?? t('common.noChange')}
        style={{ ['--fill' as string]: `${fill}%` }}
      />
      {amounts && (
        <p className="change-card__when">
          {t('whatIf.step.amounts', { before: money(amounts.before), after: money(amounts.after) })} {changeWhen(change, wire, today)}
        </p>
      )}
      <Skipped change={change} view={view} />
    </div>
  );
}

/** The two dated steps, offered only when the plan has something for them to act on. */
function StepChips({ wire, today, onAdd }: { wire: WirePlan; today: Day; onAdd: (change: Change | null) => void }) {
  const redirect = makeRedirect(wire, today);
  const percentChange = makePercent(wire, today);
  return (
    <>
      {redirect && <button type="button" className="preset-chip" onClick={() => onAdd(makeRedirect(wire, today))}>{t('whatIf.step.redirectAdd')}</button>}
      {percentChange && <button type="button" className="preset-chip" onClick={() => onAdd(makePercent(wire, today))}>{t('whatIf.step.percentAdd')}</button>}
    </>
  );
}

function NameDialog({ onClose, onSave }: { onClose: () => void; onSave: (name: string) => void }) {
  const [name, setName] = useState('');
  const id = useId();
  return (
    <Modal
      title={t('whatIf.saveScenario')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="button button--primary" disabled={!name.trim()} onClick={() => onSave(name.trim())}>{t('common.save')}</button>
        </>
      }
    >
      <Field label={t('whatIf.scenarioName')} htmlFor={id}>
        <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim() && onSave(name.trim())} />
      </Field>
    </Modal>
  );
}

function ShareDialog({ changes, years, onClose }: { changes: readonly Change[]; years: number; onClose: () => void }) {
  const wire = usePlan();
  const { refreshShares, activePlanId } = useStore();
  const toast = useToast();
  const [title, setTitle] = useState(t('whatIf.share.defaultName'));
  const [showAmounts, setShowAmounts] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [upgrade, setUpgrade] = useState<UpgradeRequired | null>(null);
  const titleId = useId();
  const urlId = useId();
  const create = async () => {
    setBusy(true);
    setUpgrade(null);
    try {
      const view = scenarioView(wire, [], changes, todayDay(), years);
      const { url: link } = await api.createShare({
        title: title.trim() || t('whatIf.share.defaultName'),
        showAmounts,
        ...(activePlanId ? { planId: activePlanId } : {}),
        scenario: { baseline: wire, changes: view.wireChanges, asOf: todayString() },
      });
      setUrl(link);
      void refreshShares();
    } catch (error) {
      const needed = upgradeOf(error);
      if (needed) setUpgrade(needed);
      else toast.push({ tone: 'error', message: t('error.saveFailed') });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={t('whatIf.share.title')}
      onClose={onClose}
      footer={url ? (
        <button type="button" className="button button--primary" onClick={onClose}>{t('common.close')}</button>
      ) : (
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="button button--primary" disabled={busy} onClick={() => void create()}>{t('whatIf.share.create')}</button>
        </>
      )}
    >
      {url ? (
        <Field label={t('whatIf.share.created')} htmlFor={urlId}>
          <div className="copy-field">
            <input id={urlId} className="input" readOnly value={url} onFocus={(e) => e.target.select()} />
            <button type="button" className="button button--outline" onClick={() => void navigator.clipboard?.writeText(url).then(() => toast.push({ tone: 'success', message: t('common.copied') }))}>
              <Icon name="copy" size={14} />{t('common.copy')}
            </button>
          </div>
        </Field>
      ) : (
        <>
          <Field label={t('whatIf.share.name')} htmlFor={titleId}>
            <input id={titleId} className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
          </Field>
          <Toggle checked={showAmounts} onChange={setShowAmounts} label={t('whatIf.share.showAmounts')} visibleLabel={<span>{t('whatIf.share.showAmounts')}</span>} />
          <p className="modal__meta">{t('whatIf.share.hint')}</p>
          {upgrade && <UpgradeNotice upgrade={upgrade} />}
        </>
      )}
    </Modal>
  );
}

// MARK: Mobile quick calculator

const QUICK: readonly { kind: PresetKind; label: 'whatIf.slider.income' | 'whatIf.slider.expenses' | 'whatIf.slider.investing'; aria: 'whatIf.slider.incomeAria' | 'whatIf.slider.expensesAria' | 'whatIf.slider.investingAria'; max: number; step: number; tone: string; icon: IconName }[] = [
  { kind: 'side', label: 'whatIf.slider.income', aria: 'whatIf.slider.incomeAria', max: 2000, step: 100, tone: 'mint', icon: 'trendUp' },
  { kind: 'spendLess', label: 'whatIf.slider.expenses', aria: 'whatIf.slider.expensesAria', max: 1000, step: 50, tone: 'amber', icon: 'arrowDown' },
  { kind: 'invest', label: 'whatIf.slider.investing', aria: 'whatIf.slider.investingAria', max: 2000, step: 100, tone: 'violet', icon: 'bars' },
];

function QuickCalculator({ note }: { note: string | null }) {
  const [amounts, setAmounts] = useState<Record<string, number>>({ side: 0, spendLess: 0, invest: 0 });
  const [years, setYears] = useState(5);
  const navigate = useNavigate();
  const [extras, setExtras] = useState<Change[]>([]);
  const changes = useMemo(
    () => [...QUICK.map((q): Change => ({ id: `quick-${q.kind}`, kind: q.kind, amount: amounts[q.kind] ?? 0 })), ...extras],
    [amounts, extras],
  );
  const view = useScenario(changes, years);
  const reset = () => {
    setAmounts({ side: 0, spendLess: 0, invest: 0 });
    setExtras([]);
  };
  const { apply, busy, canApply } = useApply(changes, reset);
  const wire = usePlan();
  const today = todayDay();
  if (!view) return null;
  return (
    <div className="page page--mobile page--with-cta">
      <header className="quick-header">
        <button type="button" className="icon-button icon-button--lg" aria-label={t('whatIf.back')} onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}>
          <Icon name="close" size={16} />
        </button>
        <div className="quick-header__titles">
          <h1 className="quick-header__title">{t('whatIf.quick.title')}</h1>
          <p className="quick-header__sub">{t('whatIf.quick.subtitle')}</p>
        </div>
        <button type="button" className="text-button" onClick={reset}>{t('common.reset')}</button>
      </header>
      <ChatBanner note={note} />
      <TimeframePicker value={years} onChange={setYears} options={[5, 10, 25, 50]} size="sm" variant="boxed" />
      <div className="card card--chart card--mint-edge">
        <CompareChart
          baseline={view.baseline}
          modified={view.modified}
          height={256}
          compact
          zoom
          diffHeight={44}
          accent="mint"
          yearAxis="relative"
          label={t('whatIf.chart.label', { years, baseline: money(view.baselineFinal), modified: money(view.modifiedFinal) })}
        />
        <ul className="legend legend--tight">
          <li><span className="legend__dash" aria-hidden="true" />{t('whatIf.currentShort')}</li>
          <li className="text-mint strong"><span className="legend__swatch legend__swatch--line" style={{ background: 'var(--mint)' }} aria-hidden="true" />{t('whatIf.withChanges')}</li>
        </ul>
      </div>
      <dl className="summary-strip">
        <div>
          <dt>{t('whatIf.extraPerMonth')}</dt>
          <dd className={view.extraPerMonth >= 0 ? 'text-mint' : 'text-amber'}>{money(view.extraPerMonth, { sign: true })}</dd>
        </div>
        <div>
          <dt>{t('whatIf.worthIn', { years })}</dt>
          <dd>{compactMoney(view.modifiedFinal)}</dd>
        </div>
        <div>
          <dt>{t('whatIf.betterOffBy')}</dt>
          <dd className={view.impact >= 0 ? 'text-mint' : 'text-amber'}><span aria-hidden="true">{view.impact >= 0 ? '↗ ' : '↘ '}</span>{compactMoney(view.impact, { sign: true })}</dd>
        </div>
      </dl>
      <div className="card card--pad-md quick-sliders">
        {QUICK.map((q) => {
          const value = amounts[q.kind] ?? 0;
          const id = `quick-${q.kind}`;
          const text = q.kind === 'spendLess' ? (value ? `−${money(value)}` : money(0)) : (value ? `+${compactMoney(value)}` : money(0));
          return (
            <div key={q.kind} className="quick-slider">
              <div className="quick-slider__head">
                <label htmlFor={id} className="quick-slider__label">
                  <span className={`quick-slider__icon quick-slider__icon--${q.tone}`} aria-hidden="true"><Icon name={q.icon} size={13} strokeWidth={2.5} /></span>
                  {t(q.label)}
                  {q.kind === 'spendLess' && <span className="quick-slider__hint">{t('whatIf.slider.expensesHint')}</span>}
                </label>
                <span className={`quick-slider__value ${value ? `text-${effectTone(q.kind)}` : 'muted'}`}>{text}</span>
              </div>
              <input
                id={id}
                type="range"
                className={`slider slider--${q.tone}`}
                min={0}
                max={q.max}
                step={q.step}
                value={value}
                aria-label={t(q.aria)}
                aria-valuetext={text}
                onChange={(e) => setAmounts((a) => ({ ...a, [q.kind]: Number(e.target.value) }))}
                style={{ ['--fill' as string]: `${(value / q.max) * 100}%` }}
              />
            </div>
          );
        })}
      </div>
      <section className="stack-sm" aria-labelledby="quick-more">
        <h2 className="section-title" id="quick-more">{t('whatIf.step.more')}</h2>
        {extras.map((c) => (
          <ChangeCard
            key={c.id}
            change={c}
            view={view}
            wire={wire}
            today={today}
            onChange={(next) => setExtras((cs) => cs.map((x) => (x.id === next.id ? next : x)))}
            onRemove={() => setExtras((cs) => cs.filter((x) => x.id !== c.id))}
          />
        ))}
        <div className="whatif-presets" role="group" aria-label={t('whatIf.step.more')}>
          <StepChips wire={wire} today={today} onAdd={(c) => c && setExtras((cs) => [...cs, c])} />
        </div>
      </section>
      <section className="stack-sm" aria-labelledby="quick-goals">
        <h2 className="section-title" id="quick-goals">{t('whatIf.goalImpact')}</h2>
        <div className="card card--list">
          <GoalImpactList goals={view.goals} />
        </div>
      </section>
      <AskAssistant view="whatIf" />
      <ResultStamp result={null} />
      <div className="cta-dock">
        <button type="button" className="button button--primary button--glow button--block button--xl" disabled={!canApply || busy} onClick={() => void apply()}>
          <Icon name="checkCircle" size={18} />
          {t('whatIf.makeReal')}
        </button>
      </div>
      <Link to="/" className="sr-only">{t('error.backHome')}</Link>
    </div>
  );
}
